import "./captcha.scss";
import { LoadingOutlined } from "@ant-design/icons";
import { Layout, Spin } from "antd";
import React from "react";
import { useLocation } from "react-router-dom";
import { DashboardFilterProvider } from "../../modules/Dashboards/hooks/DashboardFilterProvider";
import Header from "../Header";
import ApprovalCollapse from "../PendingApprovals/ApprovalCollapse";
import ApprovalDrawerHeader from "../PendingApprovals/ApprovalDrawerHeader";
import Sidebar from "../Sidebar";
import SideDrawer from "../SideDrawer";
import { useGetOrganization } from "../../../api/v1";
import { isReceiptsModuleEnabled } from "../../../utils/isReceiptsModuleEnabled";

const { Content } = Layout;
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

export const DashboardShellContext = React.createContext(false);

export const DashboardShellProvider = ({ children }: { children: React.ReactNode }) => (
  <DashboardShellContext.Provider value={true}>{children}</DashboardShellContext.Provider>
);

function getContentClassName(isDashboardRoute: boolean, className?: string) {
  if (isDashboardRoute) {
    return className || "";
  }
  return ["main-content", className].filter(Boolean).join(" ");
}

export const DashboardShell = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [isPendingApprovalsDrawerOpen, setIsPendingApprovalsDrawerOpen] = React.useState(false);
  const { organization } = useGetOrganization({
    staleTimeInfinity: true,
  });
  const isPendingApprovalEnabled = organization?.pendingApprovalButton;
  const isReceiptModuleEnabled = isReceiptsModuleEnabled(organization);

  React.useEffect(() => {
    setIsPendingApprovalsDrawerOpen(false);
  }, [location.pathname]);

  return (
    <DashboardFilterProvider>
      <Layout className="main-layout">
        <Header isPendingApprovalEnabled={isPendingApprovalEnabled} setDrawer={setIsPendingApprovalsDrawerOpen} />
        <Layout>
          <SideDrawer
            openDrawer={isPendingApprovalsDrawerOpen}
            closeDrawer={setIsPendingApprovalsDrawerOpen}
            title={<ApprovalDrawerHeader title="Pending Approvals" count={5} />}
            mask={false}
            placement="right"
            bodyStyle={{ padding: 0 }}
          >
            <ApprovalCollapse
              isReceiptModuleEnabled={isReceiptModuleEnabled}
              isPendingApprovalEnabled={isPendingApprovalEnabled}
            />
          </SideDrawer>
          <Sidebar />
          <Layout className="layout-space">{children}</Layout>
        </Layout>
      </Layout>
    </DashboardFilterProvider>
  );
};

const DashboardLayoutContent = ({
  load,
  className,
  children,
}: {
  load?: boolean;
  className?: string;
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const isDashboardRoute = location.pathname.includes("dashboard");

  return (
    <Spin spinning={load || false} indicator={antIcon} style={{ position: "fixed" }}>
      <Content className={getContentClassName(isDashboardRoute, className)}>{children}</Content>
    </Spin>
  );
};

const DashboardLayout = (props: { load?: boolean; className?: string; children: React.ReactNode }) => {
  const insideShell = React.useContext(DashboardShellContext);
  const content = <DashboardLayoutContent {...props} />;

  if (insideShell) {
    return content;
  }

  return <DashboardShell>{content}</DashboardShell>;
};

export default DashboardLayout;
